import React from 'react';
import styled, { keyframes } from 'styled-components';

// --- Keyframes ---
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.8); }
  to { opacity: 1; transform: scale(1); }
`;

// --- Styled Components ---

const MainWrapper = styled.section`
  width: 100%;
  max-width: 1300px;
  margin: 0 auto;
  /* Desktop Overlap */
  margin-top: -50px; 
  position: relative;
  z-index: 10;
  padding: 20px 30px;
  
  /* Background Animation - Unchanged */
  background: linear-gradient(-45deg, #ffffff, #00ffff, #ff00ff, #ffff00);
  background-size: 400% 400%;
  animation: ${gradientMove} 15s ease infinite;
  border-radius: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin-top: 0;
    border-radius: 0;
    padding: 20px 10px;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 15px;

  /* Desktop View: All 5 in one row */
  @media (min-width: 769px) {
    grid-template-columns: repeat(5, 1fr);
  }

  /* Mobile View: 3 in first row, 2 in second row */
  /* We use a 6-column grid to math out 3 cards (span 2) and 2 cards (span 3) */
  @media (max-width: 768px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const Card = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 25px 15px;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  animation: ${zoomIn} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) backwards;
  animation-delay: ${props => props.delay}s;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(0,0,0,0.05);

  /* Mobile Span Logic */
  @media (max-width: 768px) {
    grid-column: span 2; /* Row 1 (3 cards x 2 cols = 6) */
    
    &:nth-child(4), &:nth-child(5) {
      grid-column: span 3; /* Row 2 (2 cards x 3 cols = 6) */
    }
  }

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
    border-color: ${props => props.accentColor};
    z-index: 2;
  }
`;

const IconBox = styled.div`
  /* Font and Icon adapt to card size via clamp */
  width: clamp(50px, 8vw, 80px);
  height: clamp(50px, 8vw, 80px);
  border-radius: 15px;
  background: ${props => props.bgColor};
  color: ${props => props.iconColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;

  svg {
    width: 50%;
    height: 50%;
  }
`;

const CardTitle = styled.h3`
  font-size: clamp(0.9rem, 2.5vw, 1.2rem);
  color: red;
  font-weight: 700;
  margin-bottom: 8px;
  white-space: nowrap;
`;

const CardDesc = styled.p`
  font-size: clamp(0.7rem, 1.5vw, 0.85rem);
  color: #4a5568;
  line-height: 1.4;
  /* Keeps text from breaking card size on small screens */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// --- Data & Component ---
const services = [
  { id: 1, title: 'Diabetes', desc: 'Blood sugar monitoring.', icon: 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z', accent: '#3182ce', bg: '#ebf8ff', link: '/what-is-diabetes?' },

  { id: 2, title: 'Kidney Disorder', desc: 'Renal health care.', icon: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z', accent: '#805ad5', bg: '#faf5ff', link: '/kidney-disorder' },
    { id: 5, title: 'Obesity', desc: 'Weight management.', icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', accent: '#d69e2e', bg: '#fffff0', link: '/obesity-disorder' },
  { id: 3, title: 'Heart Disease', desc: 'Cardiac screening.', icon: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l8.78-8.78 1.06-1.06a5.5 5.5 0 000-7.78v0z', accent: '#e53e3e', bg: '#fff5f5', link: '/heart-disease' },
  { id: 4, title: 'Blood Pressure', desc: 'Pressure control.', icon: 'M22 12h-4l-3 9L9 3l-3 9H2', accent: '#38a169', bg: '#f0fff4', link: '/blood-pressure' },
  
];

const MainService = () => {
  return (
    <MainWrapper>
      <Grid>
        {services.map((service, index) => (
          <Card 
            key={service.id} 
            delay={index * 0.1} 
            accentColor={service.accent}
            onClick={() => window.location.href = service.link}
          >
            <IconBox bgColor={service.bg} iconColor={service.accent}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={service.icon} />
              </svg>
            </IconBox>
            <CardTitle>{service.title}</CardTitle>
            <CardDesc>{service.desc}</CardDesc>
          </Card>
        ))}
      </Grid>
    </MainWrapper>
  );
};

export default MainService;